import React, { useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import "react-toastify/dist/ReactToastify.css";

import PerformanceRatings from "./performanceRatings";
import RoleRatings from "./roleRatings";
import { createNewFeedback, getTalentNotes, updateFeedback } from "store/actions/talents";
import { NOTES_TYPE_UUID } from "constants";

const AddFeedback = ({
  personId,
  initialEventId,
  showOverlayLoader,
  triggerCreateFeedback,
  triggerUpdateFeedback,
  triggerTalentNotesList,
  onSuccess
}) => {
  const [eventId, setEventId] = useState("");
  const [feedback, setFeedback] = useState("");
  const [roles, setRoles] = useState([]);
  const [skills, setSkills] = useState([]);
  const [generalSkills, setGeneralSkills] = useState([]);
  const [formResetKey, setFormResetKey] = useState(0);
  // uuid of the current user's existing note for the selected event; saving then edits it (PUT)
  const [existingFeedbackUuid, setExistingFeedbackUuid] = useState(null);
  const isEditingExisting = !!existingFeedbackUuid;
  // Existing feedback opens read-only until the user clicks Edit
  const [isEditUnlocked, setIsEditUnlocked] = useState(false);
  const isLocked = isEditingExisting && !isEditUnlocked;

  const [ratings, setRatings] = useState({
    general_skills: {},
    event_roles: [],
    skills: []
  });

  const hasValidValues = (obj) => {
    const values = Object.values(obj || {});

    return (
      values.length > 0 &&
      values.every(
        (value) =>
          value !== "" &&
          value !== null &&
          value !== undefined &&
          !(typeof value === "number" && Number.isNaN(value))
      )
    );
  };

  const isSaveEnabled =
    !!eventId && hasValidValues(ratings.general_skills);

  const labelFor = (list, uuid) =>
    list.find((item) => item.uuid === uuid)?.label || uuid;

  const buildDesc = () => {
    const lines = [];

    const generalEntries = Object.entries(
      ratings.general_skills || {}
    ).filter(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        value !== ""
    );

    if (generalEntries.length) {
      lines.push("General Skills:");

      generalEntries.forEach(([uuid, value]) => {
        lines.push(
          `- ${labelFor(generalSkills, uuid)}: ${value}`
        );
      });
    }

    const addRatingSection = (label, items = []) => {
      const ratedItems = items.filter(
        (item) =>
          item.value !== null &&
          item.value !== undefined
      );

      if (ratedItems.length) {
        lines.push(`${label}:`);

        ratedItems.forEach((item) => {
          lines.push(`- ${item.label}: ${item.value}`);
        });
      }
    };

    addRatingSection("Event Roles", ratings.event_roles);
    addRatingSection("Skills", ratings.skills);

    if (feedback.trim()) {
      lines.push(
        "",
        "Additional Feedback:",
        feedback.trim()
      );
    }

    return lines.join("\n");
  };

  const toRatingMap = (items = []) =>
    items.reduce((acc, item) => {
      acc[item.uuid] = item.value ?? null;
      return acc;
    }, {});

  const resetForm = () => {
    setEventId("");
    setFeedback("");
    setExistingFeedbackUuid(null);
    setIsEditUnlocked(false);
    setRoles([]);
    setSkills([]);
    setGeneralSkills([]);

    setRatings({
      general_skills: {},
      event_roles: [],
      skills: []
    });

    setFormResetKey((prev) => prev + 1);
  };

  const submit = (e) => {
    e.preventDefault();

    if (!isSaveEnabled || isLocked) return;

    const actualPayload = {
      person_uuid: personId,
      event_uuid: eventId,
      note_type_uuid: NOTES_TYPE_UUID,
      desc: feedback.trim(),
      rating_stats: {
        general_skills: {
          ...ratings.general_skills
        },
        event_roles: toRatingMap(ratings.event_roles),
        skills: toRatingMap(ratings.skills)
      }
    };

    const payload = {
      actualPayload,

      onSuccess: () => {
        resetForm();

        triggerTalentNotesList({
          id: personId
        });

        toast.success(
          "Performance feedback submitted successfully."
        );

        onSuccess?.();
      },

      onError: (error) => {
        resetForm();

        const errorMessage =
          error?.response?.data?.msg ||
          "Unable to submit performance feedback. Please try again.";

        toast.error(
          typeof errorMessage === "string"
            ? errorMessage
            : "Unable to submit performance feedback. Please try again."
        );
      }
    };

    if (isEditingExisting) {
      updateExistingFeedback(actualPayload);
      return;
    }

    triggerCreateFeedback(payload);
  };

  const updateExistingFeedback = ({ rating_stats, desc }) => {
    triggerUpdateFeedback({
      actualPayload: {
        uuid: existingFeedbackUuid,
        rating_stats,
        desc
      },

      onSuccess: () => {
        resetForm();
        triggerTalentNotesList({ id: personId });
        toast.success("Performance feedback updated successfully.");
        onSuccess?.();
      },

      onError: (error) => {
        const errorMessage =
          error?.response?.data?.msg ||
          "Unable to update performance feedback. Please try again.";

        toast.error(
          typeof errorMessage === "string"
            ? errorMessage
            : "Unable to update performance feedback. Please try again."
        );
      }
    });
  };

  const handleRatingsAvailable = ({
    generalSkills: availableGeneral = [],
    eventRoles = [],
    skills: availableSkills = [],
    existingFeedback = null
  }) => {
    // Prefill the previous comment when editing; clear it only when leaving an existing note
    if (existingFeedback) {
      setFeedback(existingFeedback.desc || "");
    } else if (existingFeedbackUuid) {
      setFeedback("");
    }
    setExistingFeedbackUuid(existingFeedback?.uuid || null);
    setIsEditUnlocked(false);

    setGeneralSkills(availableGeneral);
    setRoles(eventRoles);
    setSkills(availableSkills);

    setRatings((prev) => ({
      ...prev,
      event_roles: [],
      skills: []
    }));
  };

  return (
    <form onSubmit={submit}>
      <div className="bg-white border-b border-b-grayBorder p-6">
        <h2 className="text-lg font-semibold">
          {isLocked
            ? "Your Feedback"
            : isEditingExisting
              ? "Edit Your Feedback"
              : "Rate Performance"}
        </h2>

        {isEditingExisting && (
          <p className="text-sm text-gray-500 mt-1">
            {isLocked
              ? "You have already reviewed this person for this event. Click Edit Review to make changes."
              : "Updating will replace your existing feedback for this event."}
          </p>
        )}

        <PerformanceRatings
          key={`performance-${formResetKey}`}
          resetKey={formResetKey}
          personId={personId}
          initialEventId={initialEventId}
          readOnly={isLocked}
          onEventSelect={setEventId}
          onRatingsAvailable={handleRatingsAvailable}
          onGeneralChange={(data, updatedGeneralSkills) => {
            setRatings((prev) => ({
              ...prev,
              general_skills: data
            }));

            if (updatedGeneralSkills) {
              setGeneralSkills(updatedGeneralSkills);
            }
          }}
        />

        {eventId && (
          <div className="mt-6">
            <RoleRatings
              key={`role-${formResetKey}`}
              resetKey={formResetKey}
              roles={roles}
              skills={skills}
              readOnly={isLocked}
              onRoleChange={(data) =>
                setRatings((prev) => ({
                  ...prev,
                  event_roles: data.event_roles || [],
                  skills: data.skills || []
                }))
              }
            />
          </div>
        )}

        <div className="mt-6">
          <label
            htmlFor="additional-feedback"
            className="mb-2 block font-semibold"
          >
            Additional Feedback{" "}
            <span className="font-normal text-gray-400">
              (optional)
            </span>
          </label>

          <div className="relative">
            <textarea
              id="additional-feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              readOnly={isLocked}
              maxLength={2048}
              rows={4}
              className="w-full rounded-lg border border-grayBorder p-3 pb-8 outline-none focus:border-blue-500"
              placeholder="Add any additional feedback not captured in the ratings above."
            />

            <span className="absolute bottom-3 right-3 text-xs text-gray-400">
              {feedback.length}/2048
            </span>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          {isLocked ? (
            <Button
              key="edit"
              htmlType="button"
              icon={<EditOutlined />}
              size="large"
              onClick={(e) => {
                e.preventDefault();
                setIsEditUnlocked(true);
              }}
            >
              Edit Review
            </Button>
          ) : (
            <button
              key="submit"
              type="submit"
              disabled={!isSaveEnabled || showOverlayLoader}
              className="bg-secondaryLight hover:bg-secondary active:bg-secondaryDark text-white font-medium text-sm px-4 py-2 rounded-lg shadow hover:shadow-lg outline-none focus:outline-none disabled:bg-gray cursor-pointer"
            >
              {showOverlayLoader ? "Saving..." : isEditingExisting ? "Update" : "Save"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

const mapStateToProps = (state) => ({
  showOverlayLoader: state.talent.showOverlayLoader
});

const mapDispatchToProps = {
  triggerCreateFeedback: createNewFeedback,
  triggerUpdateFeedback: updateFeedback,
  triggerTalentNotesList: getTalentNotes
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddFeedback);